import {
  LINK_BUTTON_CLASS,
  OUTLINE_BUTTON_CLASS,
} from "components/FlowButtons";
import { CONTINUE_BUTTON_CLASS, FOCUS_RING_CLASS } from "components/FormV2";
import { retryLabel } from "components/OtpVerification/cooldown";
import { useResendCooldown } from "components/OtpVerification/useResendCooldown";
import { useTrackLeadFlowEvent } from "pages/StartLead/analytics";
import {
  leadFlowEvents,
  type CallScreen,
  type ConfirmSurface,
} from "pages/StartLead/events";

import {
  ownSendLabel,
  SUPPORT_LINES,
  type GrantMethod,
  type SupportLineKind,
} from "./login-grant-flow";
import {
  emailButtonDisabled,
  type CallLineEmphasis,
  type EmailEmphasis,
} from "./verify-screen";

/** A text button: the flow's link style, kept legible and out of the way of
 * taps while it is disabled. */
export const TEXT_BUTTON_CLASS = `${LINK_BUTTON_CLASS} ${FOCUS_RING_CLASS} min-h-11 disabled:pointer-events-none disabled:opacity-50 disabled:hover:text-evergreen`;

export function SupportCallLine({
  kind,
  surface,
  screen,
  emphasis = "ordinary",
}: {
  kind: SupportLineKind;
  surface: ConfirmSurface;
  screen: CallScreen;
  /** Sets the line in body size and ink, for the screens where a call is the
   * way forward. */
  emphasis?: CallLineEmphasis;
}) {
  const line = SUPPORT_LINES[kind];
  const trackEvent = useTrackLeadFlowEvent();
  return (
    <p
      data-slot="call"
      className={
        emphasis === "prominent"
          ? "text-base text-soil"
          : "text-sm text-oat-700"
      }
    >
      Need help? Call us at{" "}
      <a
        href={line.href}
        className="font-bold text-soil underline"
        onClick={() =>
          trackEvent(leadFlowEvents.callClicked({ surface, screen }))
        }
      >
        {line.label}
      </a>
      . We&rsquo;re here 24/7.
    </p>
  );
}

/** Tries again once the resend cooldown, counted from `refusedAt`, is over;
 * a `refusedAt` of 0 has no wait. */
export function RetryButton({
  refusedAt,
  onRetry,
}: {
  refusedAt: number;
  onRetry: () => void;
}) {
  const { secondsLeft } = useResendCooldown({ sentAt: refusedAt, count: 1 });
  return (
    <button
      type="button"
      className={`${CONTINUE_BUTTON_CLASS} w-full`}
      onClick={onRetry}
      disabled={secondsLeft > 0}
    >
      {retryLabel(secondsLeft)}
    </button>
  );
}

const EMAIL_BUTTON_CLASSES: Record<EmailEmphasis, string> = {
  primary: CONTINUE_BUTTON_CLASS,
  outline: `${OUTLINE_BUTTON_CLASS} ${FOCUS_RING_CLASS}`,
};

/** The screen's one "Email me a link" action. `emphasis` sets only its
 * style; the box is the same either way. It is off only while its own send is
 * under way: a text still on its way never blocks it, and asking for the link
 * replaces that text. */
export function EmailLinkButton({
  emphasis,
  sending,
  onEmail,
  error = null,
}: {
  emphasis: EmailEmphasis;
  sending: GrantMethod | null;
  onEmail: () => void;
  /** Why the latest emailed link failed to send, told under the button. */
  error?: string | null;
}) {
  return (
    <div data-slot="email" className="space-y-2">
      <button
        type="button"
        className={`${EMAIL_BUTTON_CLASSES[emphasis]} w-full`}
        onClick={onEmail}
        disabled={emailButtonDisabled(sending)}
        aria-busy={sending === "email_link" || undefined}
      >
        {ownSendLabel("Email me a link", "email_link", sending)}
      </button>
      {error !== null && (
        <p role="alert" className="text-sm text-red-800">
          {error}
        </p>
      )}
    </div>
  );
}
